/**
 * Twilio access-token refresh controller for the Softphone.
 *
 * Background (2026-08-18 incident): refreshToken() was reachable from three
 * triggers (a 55-min timer re-armed by every call, the keep-alive worker's
 * age check, and the SDK's tokenWillExpire event) and every call armed ANOTHER
 * timer without cancelling the previous one. Chains multiplied ~1.5x per hour;
 * one tab reached 68 token fetches/second after ~20h and 502'd the API.
 *
 * This module makes the token lifecycle idempotent by construction:
 *   - any number of triggers coalesce onto ONE in-flight fetch
 *   - a refresh is a no-op while the current token is younger than half its TTL (≤30 min),
 *     unless `force` is set (the server told us the token is bad/expired)
 *   - exactly ONE backstop timer is armed at a time (cleared before re-arm)
 *   - refresh is deferred while a call is active (updateToken can cost ~1s of
 *     audio per Twilio's docs) — but never past HARD_DEADLINE_MS before expiry
 *   - HTTP 429 waits for Retry-After instead of escalating to device recovery
 *   - transient errors retry 5s/10s/20s, then `onExhausted` (device recovery)
 *
 * Pure JS, no React: the Softphone hands in the I/O callbacks so the policy
 * can be unit-tested with fake timers (see tokenRefreshController.test.js).
 */

// Ignore (non-forced) refresh requests for a token younger than half its TTL,
// capped at 30 min. Every legitimate trigger (SDK tokenWillExpire at ttl-5min,
// our backstop at ttl-5min, the worker age check at 50 min) fires well after
// this, so it only ever blocks spam. `force` (server says token is bad) bypasses it.
export const MIN_AGE_CAP_MS = 30 * 60 * 1000;
export const MIN_AGE_FRACTION = 0.5;
export const BACKSTOP_BEFORE_EXPIRY_MS = 5 * 60 * 1000; // own timer fires this long before expiry
export const HARD_DEADLINE_MS = 2 * 60 * 1000;   // stop deferring for an active call this close to expiry
export const CALL_DEFER_RECHECK_MS = 30 * 1000;
export const RETRY_DELAYS_MS = [5000, 10000, 20000];
// After the transient retries are exhausted, stop hitting the endpoint for this long
// (the backstop re-arms at the end of the cooldown). Bounds the failure-mode request
// rate to (1 + RETRY_DELAYS_MS.length) per cooldown ≈ 48/hour, under the server cap.
export const EXHAUSTED_COOLDOWN_MS = 5 * 60 * 1000;
// 429: HONOUR the server's Retry-After (our limiter can legitimately say up to 3600s
// at the hourly cap), floored at 90s so a tiny value can't thrash, and never escalate —
// a rate limit is the server asking for patience, not device recovery. The wait also
// sets the cooldown below, so ambient triggers (worker ticks) can't punch through:
// worst case is ONE request per 90s ≈ 40/hour.
export const MIN_429_WAIT_MS = 90 * 1000;
export const MAX_429_WAIT_MS = 3600 * 1000;
export const MIN_BACKSTOP_DELAY_MS = 30 * 1000;
// A forced refresh bypasses the age guard; floor it at one per 90s (≤40/hour of
// successful mints — under the server's 60/hour per-identity cap). Note: combined with
// a concurrent 429-retry chain the REJECTED-request worst case is ~80/hour (measured);
// rejected requests are not recorded as hits by the server limiter, so they cannot
// extend their own ban.
export const MIN_FORCE_INTERVAL_MS = 90 * 1000;

export function createTokenRefreshController({
  fetchToken,        // async () => ({ success, token, identity, expiresIn })  (throws on HTTP error, axios-style err.response.status)
  applyToken,        // (token) => void   — device.updateToken
  isCallActive,      // () => boolean
  onExhausted,       // (lastError) => void — all transient retries failed
  onSessionExpired,  // () => void — 401 from our own API
  log = () => {},
  now = () => Date.now(),
  setTimer = (fn, ms) => setTimeout(fn, ms),
  clearTimer = (id) => clearTimeout(id),
}) {
  let tokenCreatedAt = null;
  let tokenExpiresAt = null;
  let tokenTtlMs = 3600 * 1000;
  let backstopTimer = null;
  let retryTimer = null;
  let inFlight = null;       // Promise while a fetch is running
  let lastForcedAt = -Infinity;
  let transientFailures = 0;
  let cooldownUntil = 0;     // after exhaustion: no non-forced attempts before this time
  let disposed = false;
  let stats = { fetches: 0, ignoredYoung: 0, coalesced: 0, deferredForCall: 0, rateLimited: 0 };

  const clearBackstop = () => { if (backstopTimer) { clearTimer(backstopTimer); backstopTimer = null; } };
  const clearRetry = () => { if (retryTimer) { clearTimer(retryTimer); retryTimer = null; } };

  const armBackstop = () => {
    clearBackstop();
    if (!tokenExpiresAt) return;
    // Fire at expiry-5min, but never before the token is old enough to pass the
    // age guard (matters if the TTL is ever shortened below 10 min), and never
    // sooner than 1s.
    const untilEligible = minAgeMs() - tokenAgeMs();
    const untilWindow = tokenExpiresAt - BACKSTOP_BEFORE_EXPIRY_MS - now();
    const untilCooldownEnd = cooldownUntil - now();
    const delay = Math.max(MIN_BACKSTOP_DELAY_MS, untilEligible, untilWindow, untilCooldownEnd);
    backstopTimer = setTimer(() => {
      backstopTimer = null;
      refresh({ reason: 'backstop-timer' });
    }, delay);
  };

  /** Record a freshly-minted token (initial fetch or refresh). */
  const noteToken = ({ expiresIn }) => {
    if (disposed) return; // a late init on an unmounted component must not arm timers
    const ttlMs = (Number(expiresIn) > 0 ? Number(expiresIn) : 3600) * 1000;
    tokenTtlMs = ttlMs;
    tokenCreatedAt = now();
    tokenExpiresAt = tokenCreatedAt + ttlMs;
    transientFailures = 0;
    cooldownUntil = 0;
    clearRetry();
    armBackstop();
  };

  const tokenAgeMs = () => (tokenCreatedAt === null ? Infinity : now() - tokenCreatedAt);
  const minAgeMs = () => Math.min(MIN_AGE_CAP_MS, tokenTtlMs * MIN_AGE_FRACTION);
  const msToExpiry = () => (tokenExpiresAt === null ? 0 : tokenExpiresAt - now());

  // Controller-scheduled continuations (transient retry, 429 wait, after-call)
  // carry `_retry: true` so they are exempt from the forced-refresh floor —
  // otherwise a forced refresh's own retry would trip the floor and die.
  const scheduleRetry = (ms, opts) => {
    clearRetry();
    retryTimer = setTimer(() => {
      retryTimer = null;
      refresh({ ...opts, _retry: true, reason: `${opts.reason || 'refresh'}:retry` });
    }, ms);
  };

  /**
   * Request a refresh. Safe to call from any trigger, any number of times.
   * Resolves true if a new token was applied, false if the request was a no-op
   * (young token, coalesced, deferred, scheduled retry).
   */
  const refresh = async ({ force = false, reason = 'unspecified', _retry = false } = {}) => {
    if (disposed) return false;

    if (inFlight) {
      stats.coalesced += 1;
      log(`[TokenRefresh] ${reason}: refresh already in flight — coalesced`);
      return inFlight;
    }

    if (!force && tokenAgeMs() < minAgeMs()) {
      stats.ignoredYoung += 1;
      log(`[TokenRefresh] ${reason}: token is ${Math.round(tokenAgeMs() / 1000)}s old — ignored`);
      if (!backstopTimer) armBackstop(); // a no-op must never leave the lifecycle without a future trigger
      return false;
    }
    if (!force && now() < cooldownUntil) {
      stats.ignoredYoung += 1;
      log(`[TokenRefresh] ${reason}: in post-exhaustion cooldown — ignored`);
      if (!backstopTimer) armBackstop();
      return false;
    }
    if (force && !_retry) {
      // A repeating auth error (e.g. 31205 every few seconds) must not turn into a mint loop.
      if (now() - lastForcedAt < MIN_FORCE_INTERVAL_MS) {
        stats.ignoredYoung += 1;
        log(`[TokenRefresh] ${reason}: forced refresh ${Math.round((now() - lastForcedAt) / 1000)}s ago — ignored`);
        if (!backstopTimer) armBackstop();
        return false;
      }
      lastForcedAt = now();
    }

    // Defer for an active call unless we're out of runway.
    if (!force && isCallActive() && msToExpiry() > HARD_DEADLINE_MS) {
      stats.deferredForCall += 1;
      log(`[TokenRefresh] ${reason}: call active — deferring ${CALL_DEFER_RECHECK_MS / 1000}s`);
      scheduleRetry(CALL_DEFER_RECHECK_MS, { reason: 'after-call' });
      return false;
    }

    inFlight = (async () => {
      try {
        stats.fetches += 1;
        log(`[TokenRefresh] fetching new token (${reason})`);
        const resp = await fetchToken();
        if (disposed) return false; // component unmounted / device replaced while we were fetching
        if (!resp || !resp.success || !resp.token) {
          throw new Error((resp && resp.message) || 'Token endpoint returned no token');
        }
        applyToken(resp.token);
        noteToken({ expiresIn: resp.expiresIn });
        log('[TokenRefresh] token refreshed');
        return true;
      } catch (err) {
        if (disposed) return false;
        const status = err && err.response && err.response.status;
        if (status === 401) {
          log('[TokenRefresh] 401 from token endpoint — session expired');
          onSessionExpired();
          return false;
        }
        if (status === 429) {
          stats.rateLimited += 1;
          const ra = err.response.headers && (err.response.headers['retry-after'] || err.response.headers['Retry-After']);
          const waitMs = Math.min(MAX_429_WAIT_MS, Math.max(MIN_429_WAIT_MS, (parseInt(ra, 10) || 0) * 1000));
          log(`[TokenRefresh] 429 — waiting ${waitMs / 1000}s`);
          cooldownUntil = now() + waitMs; // ambient triggers (worker ticks) wait too, not just this chain
          scheduleRetry(waitMs, { force, reason: 'rate-limited' });
          return false;
        }
        transientFailures += 1;
        if (transientFailures <= RETRY_DELAYS_MS.length) {
          const delay = RETRY_DELAYS_MS[transientFailures - 1];
          log(`[TokenRefresh] fetch failed (${(err && err.message) || err}) — retry ${transientFailures} in ${delay / 1000}s`);
          scheduleRetry(delay, { force, reason: 'transient' });
          return false;
        }
        log(`[TokenRefresh] fetch failed after retries — escalating, cooling down ${EXHAUSTED_COOLDOWN_MS / 1000}s`);
        transientFailures = 0;
        cooldownUntil = now() + EXHAUSTED_COOLDOWN_MS;
        armBackstop();
        onExhausted(err);
        return false;
      } finally {
        inFlight = null;
      }
    })();
    return inFlight;
  };

  const dispose = () => {
    disposed = true;
    clearBackstop();
    clearRetry();
  };

  return {
    noteToken,
    refresh,
    dispose,
    tokenAgeMs,
    minAgeMs,
    msToExpiry,
    getStats: () => ({ ...stats }),
    // test/debug visibility
    _hasBackstop: () => backstopTimer !== null,
  };
}
